import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  Grid,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
  Button,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import ParentLayout from './Layout';
import { listEnrollments } from '../api/enrollments';
import { listLessons } from '../api/lessons';
import { listAssessments } from '../api/assessments';
import { listSubmissions } from '../api/submissions';
import analyticsApi from '../api/analytics';

const LessonsAssessments = () => {
  const [enrollments, setEnrollments] = useState([]);
  const [selectedLearner, setSelectedLearner] = useState('');
  const [lessons, setLessons] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [submissions, setSubmissions] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [weeklyLessons, setWeeklyLessons] = useState([]);
  const [reportLink, setReportLink] = useState(null);

  useEffect(() => {
    return () => {
      if (reportLink) {
        URL.revokeObjectURL(reportLink);
      }
    };
  }, [reportLink]);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const enr = await listEnrollments({ status: 'active' });
        setEnrollments(enr || []);
        const first = enr?.[0]?.studentId?._id || '';
        setSelectedLearner(first);
      } catch (err) {
        setError(err?.response?.data?.message || 'Failed to load learners');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    const loadContent = async () => {
      try {
        setLoading(true);
        const enrollment = enrollments.find((enr) => enr.studentId?._id === selectedLearner);
        if (!enrollment) {
          setLessons([]);
          setAssessments([]);
          setSubmissions({});
          setWeeklyLessons([]);
          setReportLink(null);
          return;
        }

        const subjectIds = new Set(
          (enrollment.subjects || [])
            .map((subject) => (subject?._id || subject))
            .filter(Boolean)
            .map((id) => id.toString())
        );
        const grade = enrollment.grade;
        const sectionId = enrollment.sectionId?._id || enrollment.sectionId || undefined;

        const [lessonData, assessmentData, submissionData] = await Promise.all([
          listLessons({ availableOnly: true, grade, sectionId }),
          listAssessments({ grade, sectionId }),
          listSubmissions({ studentId: selectedLearner }),
        ]);

        const lessonList = Array.isArray(lessonData) ? lessonData : [];
        const filteredLessons = subjectIds.size
          ? lessonList.filter((lesson) => subjectIds.has(String(lesson.subjectId)))
          : lessonList;
        setLessons(filteredLessons);

        const now = new Date();
        const start = new Date(now);
        start.setDate(now.getDate() - 7);
        const weekly = filteredLessons.filter((lesson) => {
          const ts = lesson.availabilityStart || lesson.createdAt;
          if (!ts) return false;
          const dt = new Date(ts);
          return dt >= start && dt <= now;
        });
        setWeeklyLessons(weekly);

        const assessmentList = Array.isArray(assessmentData) ? assessmentData : [];
        const filteredAssessments = subjectIds.size
          ? assessmentList.filter((assessment) => subjectIds.has(String(assessment.subjectId)))
          : assessmentList;
        setAssessments(filteredAssessments);

        const submissionMap = {};
        (submissionData || [])
          .filter((s) => String(s.studentId) === String(selectedLearner))
          .forEach((s) => {
            const key = `${s.assessmentId}:${s.studentId}`;
            submissionMap[key] = s;
          });
        setSubmissions(submissionMap);

        const blob = await analyticsApi.getTermReport({ studentId: selectedLearner });
        if (blob) {
          const url = URL.createObjectURL(blob);
          setReportLink(url);
        } else {
          setReportLink(null);
        }
      } catch (err) {
        setError(err?.response?.data?.message || 'Failed to load content');
      } finally {
        setLoading(false);
      }
    };
    loadContent();
  }, [selectedLearner, enrollments]);

  return (
    <ParentLayout>
      <Box>
        <Stack spacing={3}>
          <Box>
            <Typography variant="h4" fontWeight={800} color="primary.main">
              Lessons & Assessments
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Browse published lessons and track assessment submissions for your learner.
            </Typography>
          </Box>

          {(loading) && <LinearProgress />}
          {error && <Alert severity="error">{error}</Alert>}

          <TextField
            select
            label="Select learner"
            value={selectedLearner}
            onChange={(e) => setSelectedLearner(e.target.value)}
            sx={{ maxWidth: 320 }}
          >
            {enrollments.map((enr) => (
              <MenuItem key={enr.studentId?._id} value={enr.studentId?._id}>
                {`${enr.studentId?.name || ''} ${enr.studentId?.surname || ''} (${enr.grade ? `G${enr.grade}` : ''})`}
              </MenuItem>
            ))}
          </TextField>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardHeader title="Weekly lesson summary" />
                <CardContent>
                  {weeklyLessons.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No lessons published in the last 7 days.
                    </Typography>
 ) : (
                    <Stack spacing={1}>
                      <Typography variant="body2" color="text.secondary">
                        {weeklyLessons.length} lessons published in the last 7 days.
                      </Typography>
                      {weeklyLessons.map((lesson) => (
                        <Stack key={lesson._id} spacing={0.5} divider={<Divider flexItem />}>
                          <Typography fontWeight={700}>{lesson.title}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {lesson.description || 'No description'}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardHeader title="Lessons" />
                <CardContent>
                  <Stack spacing={1}>
                    {lessons.length === 0 && (
                      <Typography variant="body2" color="text.secondary">No lessons available.</Typography>
                    )}
                    {lessons.map((lesson) => (
                      <Stack key={lesson._id} spacing={0.5}>
                        <Typography fontWeight={700}>{lesson.title}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {lesson.description || 'No description'}
                        </Typography>
                        {Array.isArray(lesson.objectives) && lesson.objectives.length > 0 && (
                          <Stack spacing={0.5}>
                            <Typography variant="caption" color="text.secondary">Objectives</Typography>
                            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                              {lesson.objectives.map((obj, idx) => (
                                <Chip key={`${lesson._id}-obj-${idx}`} size="small" label={obj} />
                              ))}
                            </Stack>
                          </Stack>
                        )}
                        <Stack direction="row" spacing={1}>
                          <Chip size="small" label={`G${lesson.grade}`} />
                          <Chip size="small" label={`Files: ${lesson.files?.length || 0}`} />
                          {lesson.quiz?.type === 'embedded' && (
                            <Chip size="small" color="secondary" label="Quiz included" />
                          )}
                        </Stack>
                        {(lesson.files || []).map((file) => (
                          <Button
                            key={file.key}
                            startIcon={<DownloadIcon />}
                            size="small"
                            href={file.url}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {file.originalName}
                          </Button>
                        ))}
                      </Stack>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardHeader title="Assessments" />
                <CardContent>
                  <Stack spacing={1}>
                    {assessments.length === 0 && (
                      <Typography variant="body2" color="text.secondary">No assessments.</Typography>
                    )}
                    {assessments.map((assessment) => {
                      const existing = submissions[`${assessment._id}:${selectedLearner}`];
 const dueDate = assessment.dueDate ? new Date(assessment.dueDate).getTime() : null;
                      const submittedAt = existing?.createdAt ? new Date(existing.createdAt).getTime() : null;
                      const isLate = dueDate != null && submittedAt != null && submittedAt > dueDate;
                      const isMissed = dueDate != null && submittedAt == null && Date.now() > dueDate;
                      return (
                        <Stack key={assessment._id} spacing={0.5}>
                          <Typography fontWeight={700}>{assessment.title}</Typography>
                          <Typography variant="body2" color="text.secondary">
 Due: {assessment.dueDate ? new Date(assessment.dueDate).toLocaleString() : 'TBC'}
                          </Typography>
                          <Stack direction="row" spacing={1}>
                            <Chip size="small" label={assessment.type?.toUpperCase()} />
                            <Chip size="small" label={`Marks: ${assessment.totalMarks || 0}`} />
                            {existing ? (
                              <Chip size="small" color="success" label={`Submitted (${existing.status})`} />
 ) : (
                              <Chip size="small" color="warning" label="Not submitted" />
                            )}
                            {isLate && <Chip size="small" color="warning" label="Late" />}
                            {isMissed && <Chip size="small" color="error" label="Missed" />}
                          </Stack>
                          {assessment.attachments?.map((file) => (
                            <Button
                              key={file.key}
                              startIcon={<DownloadIcon />}
                              size="small"
                              href={file.url}
                              target="_blank"
                              rel="noreferrer"
                            >
                              {file.originalName}
                            </Button>
                          ))}
                        </Stack>
                      );
                    })}
                    <Divider sx={{ my: 1 }} />
                    <Stack spacing={1}>
                      <Typography variant="body2" color="text.secondary">
                        Progress reports
                      </Typography>
                      {reportLink ? (
                        <Button
                          variant="outlined"
                          size="small"
                          href={reportLink}
                          target="_blank"
                          rel="noreferrer"
                          startIcon={<DownloadIcon />}
                        >
                          Download term report
                        </Button>
 ) : (
                        <Chip size="small" label="Report not ready" />
                      )}
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Stack>
      </Box>
    </ParentLayout>
  );
};

export default LessonsAssessments;
